<script setup>
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '@/stores/authStore.js'
import { userApi } from '@/api/User/UserApi.js'
import { Toaster } from '@/models/utils/Toaster.js'
import LoadingSpinner from '@/components/Spinners/LoadingSpinner.vue'
import Combobox from '@/components/Forms/Combobox.vue'
import InputText from '@/components/Forms/InputText.vue'

const toaster = new Toaster()

const tenant = ref(null)
const tenant_settings = ref({})
const modules = ref([])
const login_screen_message = ref(null)
const support_message_subjects = ref(null)
const support_message_subjects_preview_selection = ref(null)
const submit_warning_text = ref(null)
const isSaving = ref(false)
const isLoading = ref(false)
const error = ref(null)
const dataSubmitEnabled = ref(false)

const support_message_subjects_preview_items = computed(() => {
	return support_message_subjects.value?.split('\n').map(function(item) {
		return item.trim();
	}).filter(function(item) {
		return item != null && item !== ''
	})
})

onMounted(() => {
	isLoading.value = true

	userApi.getTenantById(useAuthStore().getAuth.user().tenant_id, (tenantData) => {
		tenant.value = tenantData;
		tenant_settings.value = JSON.parse(tenant.value.settings_json);

		//Modules to check if enabled.
		modules.value = tenant_settings.value.modules.value;

		//Login
		login_screen_message.value = tenant_settings.value.login_screen_message.value;

		//Support Messages
		support_message_subjects.value = tenant_settings.value.support_message_subjects.value;

		//Data Submissions
		dataSubmitEnabled.value = isEnabled('data.submit-data');
		submit_warning_text.value = tenant_settings.value.data_submissions.submit_warning_text.value;

		isLoading.value = false;
	})
})

/**
 * Checks if route is enabled.
 * @param p_route
 * @returns {*|boolean}
 */
function isEnabled(p_route)
{
	const module = modules.value.find((p_item) => p_item.route === p_route);
	if (module)
	{
		return module.enabled;
	}
	return false;
}

/**
 * Save settings.
 * Note, SanitizeInput middleware will strip out html/script tags.
 */
function save() {
	const settings = {
		'support_message_subjects': support_message_subjects.value,
		'login_screen_message': login_screen_message.value
	};

	if (dataSubmitEnabled.value)
	{
		settings['data_submissions'] = {
			'submit_warning_text': submit_warning_text.value,
		};
	}

	isSaving.value = true
	userApi.updateTenantSettings({ settings: settings },
		() => {
			isSaving.value = false
			toaster.success('Settings successfully updated!')

			userApi.refreshUserData()
		}, (e) => {
			error.value = e.showErrorBanner()
			isSaving.value = false
		})
}

</script>

<template>
	<LoadingSpinner v-if="isLoading" />

	<fieldset v-if="dataSubmitEnabled">
		<legend>
			Data Submission Settings
		</legend>
		<div class="fieldset-container">
			<InputText class="w-full" label="Submission Warning Text" type="textarea" text-area-rows="2" v-model="submit_warning_text" :errors="error" error-field="settings.submit_warning_text"
						  tooltip-location="right" tooltip-width="w-fit" :tooltip="tenant_settings.data_submissions?.submit_warning_text?.description"/>
		</div>
	</fieldset>

	<div v-if="!isLoading">
		<fieldset>
			<legend>
				Login
			</legend>
			<div class="fieldset-container">
				<InputText class="w-full" label="Login screen message" type="textarea" text-area-rows="2" v-model="login_screen_message" :errors="error" error-field="settings.login_screen_message"
							  tooltip-location="right" tooltip-width="w-fit" :tooltip="tenant_settings.login_screen_message?.description"/>
			</div>
		</fieldset>

		<fieldset>
			<legend>
				Support Messages
			</legend>
			<div class="fieldset-container flex space-x-4">
				<InputText label="Support message subjects (one per line)" type="textarea" v-model="support_message_subjects" text-area-rows="3" input-width="w-75" :errors="error" error-field="settings.support_message_subjects" tooltip-location="right" tooltip-width="w-fit" :tooltip="tenant_settings.support_message_subjects?.description"/>
				<Combobox id="combo_support_message_subjects" v-model="support_message_subjects_preview_selection" :items="support_message_subjects_preview_items" title="Preview Subjects" placeholder="Select subject" input-height="h-full" :readonly="true" />
			</div>
		</fieldset>

		<SubmitButton label="Save" @click="save" size="small" :disabled="isSaving" :loading="isSaving"/>
	</div>
</template>

<style scoped>
fieldset{
	@apply border border-grayscale border-opacity-30 px-2 mb-4;
}
legend{
	@apply px-2 font-bold;
}
fieldset button{
	@apply m-2;
}
.fieldset-container
{
	@apply p-2;
}
</style>